<template>
  <div class="lab-modal flat-scroll">
    <div class="lab-modal__modal">
      <div class="lab-modal__wrapper">
        <div class="lab-modal__content-wrapper">
          <TitleBarLab
            :lab="currentLab"
            :isLoading="isLoading"
            :parentContext="parentContext"
            @close-modal="closeModal"
            @start-issue="$emit('start-issue')"
          />
          <SkillDiveTopBar
            v-if="
              isSkillDiveB2CUser && currentLab.is_lab_experience && !isLoading
            "
            class="lab-modal__skill-dive-bar"
          />
          <div v-if="!isLoading" class="lab-modal__body">
            <div class="lab-modal__left">
              <div v-if="filteredTabs.length > 1" class="lab-modal__tabs-nav">
                <div
                  v-for="tab in filteredTabs"
                  :key="tab.key"
                  :class="[
                    'lab-modal__tabs-nav-item',
                    { active: activeTab === tab.key },
                  ]"
                  @click="handleTabClick(tab.key)"
                >
                  {{ tab.key }}
                </div>
              </div>
              <div class="lab-modal__description">
                <div
                  v-if="
                    activeTab === 'overview' && currentLab.explanation_video
                  "
                  class="lab-modal__explanation-video"
                >
                  <IsolatedVideo
                    :videoId="currentLab.explanation_video"
                    :autostart="false"
                    trigger="lab"
                  />
                </div>
                <div
                  v-if="activeTab"
                  ref="description"
                  :key="tabKey"
                  v-replace-imgs
                  class="lab-modal__description-wrapper markdown-styles"
                  v-html="activeTabContent(activeTab)"
                />
              </div>
            </div>
            <div class="lab-modal__right">
              <Card
                v-if="notSessionLab"
                :isLoading="isLoading"
                :lab="currentLab"
                :setLabStatus="setLabStatus"
              />
              <SessionLabCard
                v-else
                :lab="currentLab"
                :setLabStatus="setLabStatus"
                :isLoading="isLoading"
                :parentContext="parentContext"
                :mixpanelEventPayload="mixpanelEventPayload"
                :labsRequestPayload="labsRequestPayload"
                @close-modal="closeModal"
              />
              <div
                v-if="currentLab.solution_video"
                class="lab-modal__button-wrapper"
              >
                <a class="btn__link" @click.stop="toggleVideoPlayer()">
                  Reveal Solution
                </a>
              </div>
            </div>
          </div>
          <div v-else class="lab-modal__body__loader">
            <ActionLoader name="ball-clip-rotate" :size="1.5" />
          </div>
          <SingleVideo
            v-if="isPlayingSolutionVideo && currentLab.solution_video"
            :videoId="currentLab.solution_video"
            trigger="lab"
            @update-status="updateStatus"
            @close-video-player="toggleVideoPlayer"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { highlightAllUnder } from 'prismjs'
import SingleVideo from '@/components/media/SingleVideo.vue'
import IsolatedVideo from '@/components/media/IsolatedVideo.vue'
import CONSTANTS from '@/constants'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import TitleBarLab from '@/components/modals/lab-modal/TitleBarLab.vue'
import Card from '@/components/modals/lab-modal/Card.vue'
import SessionLabCard from '@/components/modals/lab-modal/SessionLabCard.vue'
import { contentCommons, labCommons } from '@/helpers/mixins'
import { labContentParser } from '@/helpers/contentParsers'
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
} from '@/helpers/localStorage'
import SkillDiveTopBar from '@/components/lab-experience/SkillDiveTopBar.vue'
import DraggableImage from '@/components/shared/DraggableImage.vue'
import '@/assets/css/prism-theme.css'
import 'prismjs/components/prism-python'

const TABS_CONFIG = [
  { key: 'overview', contentKey: 'description_html' },
  { key: 'tasks', contentKey: 'tasks_html' },
  { key: 'solutions', contentKey: 'solutions_html' },
]

const CONTENT_PARSERS = [
  {
    target: '#b#',
    openTag: '<b>',
    closeTag: '</b>',
  },
  {
    target: '__',
    openTag: '<span class="lab-modal--remarked">',
    closeTag: '</span>',
  },
]

export default {
  name: 'LabModal',
  components: {
    TitleBarLab,
    Card,
    SkillDiveTopBar,
    SingleVideo,
    IsolatedVideo,
    SessionLabCard,
    ActionLoader,
  },
  mixins: [contentCommons, labCommons],
  beforeRouteEnter(to, from, next) {
    next(() => {
      if (from && from.path) {
        setLocalStorage('prevRoute', from.path)
      }
    })
  },
  props: {
    parentContext: {
      type: Object,
      default: () => {},
    },
  },
  data: () => {
    return {
      tabKey: 9999,
      isPlayingSolutionVideo: false,
      hasUpdatedStatus: false,
      isLoading: false,
      activeTab: null,
    }
  },
  computed: {
    ...mapState({
      currentLabSession: state => state.labs.currentLabSession,
      currentCourse: state => state.courses.currentCourse,
      currentBootcamp: state => state.bootcamps.selectedBootcamp,
      currentLearningPath: state => state.learningPaths.currentLearningPath,
      currentPlaylists: state => state.playlists.playlistCache,
      currentCollection: state => state.labExperience.currentCollection,
      isAuthenticated: state => state.auth.isAuthenticated,
      contentIssues: state => state.courses.contentIssues,
    }),
    ...mapGetters({
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
    }),
    notSessionLab() {
      return (
        this.currentLab.lab_type === 'github' ||
        this.currentLab.lab_type === 'azure'
      )
    },
    filteredTabs() {
      return TABS_CONFIG.filter(tab => !!this.currentLab[tab.contentKey])
    },
    mixpanelEventPayload() {
      let payload = {
        lab_id: this.currentLab.id,
        lab_name: this.currentLab.name,
        lab_type: this.currentLab.lab_type,
        lab_context: this.parentContext.type,
        ...this.currentLab,
      }
      const {
        COURSE,
        LEARNING_PATH,
        BOOTCAMP,
        PLAYLIST,
        COLLECTION,
      } = CONSTANTS.PARENT_CONTENT_TYPES_KEYS

      switch (this.parentContext.type) {
        case COURSE: {
          const { id: course_id, name: course_name } = this.currentCourse

          payload = { ...payload, course_id, course_name }
          break
        }
        case LEARNING_PATH: {
          const {
            id: learningPath_id,
            name: learningPath_name,
          } = this.currentLearningPath

          payload = { ...payload, learningPath_id, learningPath_name }
          break
        }
        case BOOTCAMP: {
          const { id: bootcamp_id, name: bootcamp_name } = this.currentBootcamp

          payload = { ...payload, bootcamp_id, bootcamp_name }
          break
        }
        case PLAYLIST: {
          payload = {
            ...payload,
            playlist_id: this.parentContext.id,
          }
          break
        }
        case COLLECTION: {
          const {
            id: collection_id,
            name: collection_name,
          } = this.currentCollection

          payload = { ...payload, collection_id, collection_name }
          break
        }
      }

      return payload
    },
    labsRequestPayload() {
      return {
        id: this.currentLab.id,
        parentId: this.parentContext.id,
        parentType: this.parentContext.type,
      }
    },
  },
  async created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    if (this.labNotAvailable) this.closeModal()
    this.activeTab = this.filteredTabs?.[0]?.key
    this.updatePrism()
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  mounted() {
    if (this.isAuthenticated) {
      this.getRunningLab()
      if (!this.contentIssues) {
        this.getContentIssues()
      }
    }
  },
  methods: {
    ...mapActions('playlists', ['getPlaylistById']),
    ...mapActions('content', ['getContentIssues']),
    ...mapActions('labs', ['getRunningLab']),
    handleTabClick(newTab) {
      this.tabKey = this.generateTabKey()
      this.activeTab = newTab
    },
    async closeModal() {
      this.$store.dispatch('content/setPlayingVideoId', '')
      this.$store.dispatch('content/setCurrentQuizId', '')
      this.$emit('prev-route')
    },
    async setLabStatus(status = CONSTANTS.CONTENT_USER_STATUS.STARTED) {
      const userLabStatus = await this.$store.dispatch(
        'content/updateLabStatus',
        {
          labId: this.currentLab.id,
          status,
          parentId: this.parentContext.id,
          parentType: this.parentContext.type,
        }
      )

      if (status === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        this.getRunningLab()
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_LAB_STARTED,
          this.mixpanelEventPayload
        )
      } else if (status === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_LAB_FINISHED,
          this.mixpanelEventPayload
        )
      }

      return userLabStatus
    },
    async updatePrism() {
      if (!this.$refs.description) return
      await this.$nextTick()
      highlightAllUnder(this.$refs.description)
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$']],
            displayMath: [['$$', '$$']],
            processEscapes: true,
            processEnvironments: true,
          },
          // Center justify equations in code and markdown cells. Elsewhere
          // we use CSS to left justify single line equations in code cells.
          displayAlign: 'center',
          'HTML-CSS': {
            styles: { '.MathJax_Display': { margin: 0 } },
            linebreaks: { automatic: true },
          },
        })
        window.MathJax.Hub.Queue([
          'Typeset',
          window.MathJax.Hub,
          this.$refs.description,
        ])
      }
    },
    toggleVideoPlayer() {
      this.isPlayingSolutionVideo = !this.isPlayingSolutionVideo
    },
    async updateStatus() {
      if (!this.hasUpdatedStatus && this.videoId) {
        this.hasUpdatedStatus = true
        await this.$store.dispatch('content/updateVideoStatus', {
          videoId: this.videoId,
          status: CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        })
      }
    },
    activeTabContent(activeTab) {
      return this.filteredTabs.length
        ? this.labContentParsed(
            this.currentLab[
              this.filteredTabs.find(tab => tab.key === activeTab).contentKey
            ]
          )
        : ''
    },
    labContentParsed(content) {
      const htmlContent = document.createElement('html')
      htmlContent.innerHTML = content
      const codeContent = htmlContent.getElementsByTagName('code')
      if (!codeContent?.length) return content
      for (let i = 0; i < codeContent.length; i++) {
        CONTENT_PARSERS.forEach(parser => {
          const codeElement = codeContent[i]
          codeElement.innerHTML = labContentParser(
            parser,
            codeElement.innerHTML
          )
        })
      }
      return htmlContent.innerHTML
    },
    generateTabKey() {
      return Math.floor(1000 + Math.random() * 9000)
    },
  },
}
Vue.directive('replace-imgs', {
  bind(el) {
    const imgs = el.querySelectorAll('img')
    imgs.forEach(img => {
      const imgSrc = img.getAttribute('src')
      const draggableImageComponent = new (Vue.extend(DraggableImage))({
        propsData: { imgSrc },
      }).$mount()

      img.replaceWith(draggableImageComponent.$el)
    })
  },
})
</script>

<style lang="scss">
.lab-modal {
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--dark-gray);
    z-index: 205;
    overflow-y: auto;
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 206;
    position: relative;
  }
  &__content-wrapper {
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    max-width: 2800px;
    height: 100%;
  }
  &__left {
    padding: 30px 40px;
    background: linear-gradient(
      112.2deg,
      rgba(187, 188, 199, 0.4) -46.39%,
      rgba(187, 188, 199, 0) 112.38%
    );
  }
  &__tabs-nav {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    &-item {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 34px;
      padding: 0 15px;
      border-radius: 40px;
      background: rgba(255, 255, 255, 0);
      font-size: 12px;
      line-height: 14px;
      text-transform: capitalize;
      color: var(--light-blue-gray);
      transition: color 0.3s, background-color 0.3s;
      cursor: pointer;
      &:not(:last-child) {
        margin-right: 20px;
      }
      &:hover,
      &.active {
        background: rgba(255, 255, 255, 1);
        color: var(--charcoal);
      }
    }
  }
  &__tabs-nav {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    &-item {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 34px;
      padding: 0 15px;
      border-radius: 40px;
      background: rgba(255, 255, 255, 0);
      font-size: 12px;
      line-height: 14px;
      text-transform: capitalize;
      color: var(--light-blue-gray);
      transition: color 0.3s, background-color 0.3s;
      cursor: pointer;
      &:not(:last-child) {
        margin-right: 20px;
      }
      &:hover,
      &.active {
        background: rgba(255, 255, 255, 1);
        color: var(--charcoal);
      }
    }
  }
  &__description {
    color: white;
    font-size: 16px;
    .prism-code,
    .codehilite {
      code {
        background: black;
      }
    }
    .codehilite {
      font-size: 0.95em;
      background: black;
      height: auto;
      display: block;
      width: 100%;
      margin-bottom: 20px;
      margin-top: 20px;
      overflow: initial;
      .token {
        white-space: break-spaces;
      }
    }
    table {
      background: #3d3d3d;
      border-radius: 0.25em;
      border-collapse: collapse;
      margin: 1em;
      margin-bottom: 20px;
    }
    th {
      border-bottom: 1px solid #364043;
      color: var(--orange);
      font-size: 0.85em;
      font-weight: 600;
      padding: 0.5em 1em;
      text-align: left;
    }
    td {
      color: #fff;
      font-weight: 400;
      padding: 0.65em 1em;
    }
    tbody tr {
      transition: all 0.25s ease;
    }
    tbody tr:hover {
      background: var(--flat-black);
    }
  }
  &__right {
    padding: 50px;
    align-items: center;
    text-align: center;
    background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    .solution-button-wrapper {
      width: 500px;
      margin-top: 20px;
      display: flex;
      .solution-button {
        margin-left: auto;
        cursor: pointer;
        border-radius: 15px;
        padding: 5px 10px;
        font-size: 11px;
        color: white;
        border: 1px solid var(--orange);
        margin-top: 10px;
      }
    }
  }

  @include tablet {
    &__body {
      display: flex;
      width: 100%;
      height: calc(100% - 100px);
      overflow: hidden;
      &__loader {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
      }
    }
    &__left {
      width: 50%;
      height: 100%;
      overflow: auto;
    }
    &__right {
      width: 50%;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: auto;
    }
  }
  &__button-wrapper {
    margin-left: auto;
  }
  &__description {
    &-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      flex: 1;
      img {
        max-width: 100%;
      }
    }

    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .lds-ring {
    display: inline-block;
    position: relative;
    width: 15px;
    height: 15px;
    padding-top: 2px;
    div {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 15px;
      height: 15px;
      border: 2px solid rgb(129, 129, 129);
      border-radius: 50%;
      animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: rgb(129, 129, 129) transparent transparent transparent;
    }
    div:nth-child(1) {
      animation-delay: -0.45s;
    }
    div:nth-child(2) {
      animation-delay: -0.3s;
    }
    div:nth-child(3) {
      animation-delay: -0.15s;
    }
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  &--remarked {
    background: var(--tango);
    color: var(--shark);
  }
}
</style>
